import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Loading from "../common/Loading";
import { ACCENT, SecondaryButton } from "./shared";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";
import { WALLET_MESSAGES, walletApi, walletErrorMessage } from "./walletApi";
import { formatDate, formatMoney } from "./walletFormat";

// Artist Studio > Music Wallet > Payout history: the signed-in artist's own withdrawals (the API only ever returns those),
// newest first, with the amount, the day it was requested, where it was sent and its status. A payout that is still
// processing is shown as processing; a failed one says why and, when the bank account is the problem, asks for new details.

export const PAYOUT_PILL = {
  paid: "bg-green-100 text-green-800",
  processing: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-700",
  skipped: "bg-gray-100 text-gray-700",
};

function PayoutBody() {
  const [records, setRecords] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const latest = useRef(0);

  const load = useCallback(() => {
    const ticket = latest.current + 1;
    latest.current = ticket;
    setError("");
    setRecords(null);
    walletApi.payouts({ limit: 25 }).then(
      (r) => {
        if (ticket !== latest.current) return;
        setRecords(r?.records || []);
        setNextCursor(r?.has_more ? r.next_cursor : null);
      },
      (e) => {
        if (ticket !== latest.current) return;
        setError(walletErrorMessage(e, WALLET_MESSAGES.payouts));
        setRecords([]);
      }
    );
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await walletApi.payouts({ limit: 25, cursor: nextCursor });
      setRecords((prev) => [...(prev || []), ...(r?.records || [])]);
      setNextCursor(r?.has_more ? r.next_cursor : null);
    } catch (e) {
      setError(walletErrorMessage(e, WALLET_MESSAGES.payouts));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div data-testid="payout-history">
      <Link to="/artist-studio/wallet" className="text-sm font-semibold text-gray-700 hover:underline">← Back to Music Wallet</Link>
      <h1 className="mt-4 text-[32px] font-bold leading-tight text-gray-900">Payout history</h1>

      {error && (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert" data-testid="payouts-error">
          {error} <button type="button" className="underline font-semibold" onClick={load}>Try again</button>
        </p>
      )}

      <div className="mt-5 overflow-x-auto rounded-2xl bg-[#f1f6f2]">
        {records === null ? (
          <div className="flex justify-center py-16"><Loading /></div>
        ) : records.length === 0 ? (
          !error && <p className="p-8 text-center text-gray-500" data-testid="payouts-empty">No payouts yet. Withdrawals and scheduled payouts will show here.</p>
        ) : (
          <table className="w-full text-left text-[15px]">
            <thead className="text-xs font-bold uppercase tracking-wide" style={{ color: ACCENT }}>
              <tr>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Reference</th>
                <th className="px-6 py-4">Amount</th>
                <th className="px-6 py-4">Method</th>
                <th className="px-6 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {records.map((p) => (
                <tr key={p.id} className="border-t border-white/70 align-top" data-testid="payout-row">
                  <td className="whitespace-nowrap px-6 py-4 text-gray-900">{formatDate(p.requested_at)}</td>
                  <td className="px-6 py-4 text-gray-700" data-testid="payout-reference">
                    <span className="font-semibold text-gray-900">{p.reference || `PAY-${String(p.id).padStart(5, "0")}`}</span>
                    {p.provider_reference && <span className="block break-all text-xs text-gray-500">{p.provider_reference}</span>}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 font-semibold text-gray-900">{formatMoney(p.amount_minor, p.currency)}</td>
                  <td className="px-6 py-4 text-gray-700">{p.destination}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${PAYOUT_PILL[p.status] || PAYOUT_PILL.processing}`} data-testid="payout-status">{p.status_label}</span>
                    {p.status === "failed" && (
                      <p className="mt-1.5 max-w-xs text-sm text-gray-700" data-testid="payout-failure">
                        {p.failure_reason || "The payout could not be completed."} The money is back in your wallet.
                        {p.update_payout_details && <> Please update your bank details in Stripe before you try again.</>}
                      </p>
                    )}
                    {p.status === "skipped" && (
                      <p className="mt-1.5 max-w-xs text-sm text-gray-700" data-testid="payout-skipped">
                        {p.skip_message || "No payout was made for this period."} Nothing was taken from your wallet{p.carried_over ? ": your balance stays there and is included in your next payout" : ""}.
                      </p>
                    )}
                    {p.status === "processing" && <p className="mt-1.5 text-sm text-gray-500">On its way. We'll tell you when it is paid.</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {nextCursor && (
        <div className="mt-5 flex justify-center">
          <SecondaryButton onClick={loadMore} disabled={loadingMore} data-testid="payouts-more">{loadingMore ? "Loading…" : "Load more"}</SecondaryButton>
        </div>
      )}
    </div>
  );
}

export default function PayoutHistory() {
  const account = useStudioAccount();
  return (
    <StudioGate account={account} active="wallet" heading="" tabs={[]} maxWidth="max-w-4xl">
      <PayoutBody />
    </StudioGate>
  );
}
